import React from 'react';
import { Box, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import useNotify from '../../hooks/useNotify';
import { useTranslation } from 'react-i18next';
import { useResetPasswordRequest } from '@openapi/auth/auth';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import TikiLayout, { TikiInput, TikiButton, tiki } from './TikiLayout';

function TikiForgotPasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const resetPasswordRequestMutation = useResetPasswordRequest({
        mutation: {
            onSuccess: () => {
                notifySuccess(t('login.forgotPasswordNotifySuccess'));
                navigate('/?theme=tiki');
            },
            onError: notifyError,
        },
    });

    const onSubmit = async (values, { setSubmitting }) => {
        resetPasswordRequestMutation.reset();
        try {
            await resetPasswordRequestMutation.mutateAsync({ data: { email: values.email } });
        } catch (_error) {
            // onError already surfaced notification above
        } finally {
            setSubmitting(false);
        }
    };

    const validateSchema = Yup.object().shape({
        email: Yup.string().email(t('login.wrongEmailFormat')).required(t('common.required')),
    });

    return (
        <TikiLayout>
            <Box sx={{ mb: 5, textAlign: 'left' }}>
                <Typography
                    variant="h1"
                    sx={{
                        fontFamily: tiki.headline,
                        fontWeight: 800,
                        fontSize: '2.25rem',
                        color: tiki.colors.surface,
                        mb: 1.5,
                        letterSpacing: '-0.02em',
                    }}
                >
                    Lost Your Flame?
                </Typography>
                <Typography
                    sx={{
                        fontFamily: tiki.body,
                        fontSize: '1.125rem',
                        color: tiki.colors.textMuted,
                    }}
                >
                    No worries — enter your Island ID and we&apos;ll relight the torch.
                </Typography>
            </Box>

            <Formik
                initialValues={{ email: '' }}
                validationSchema={validateSchema}
                onSubmit={onSubmit}
            >
                {({ isSubmitting, dirty }) => {
                    const isDisabled =
                        isSubmitting || !dirty || resetPasswordRequestMutation.isPending;
                    return (
                        <Form>
                            <TikiInput
                                name="email"
                                label="Island ID"
                                type="email"
                                placeholder="your@email.com"
                                icon={<EmailOutlinedIcon />}
                            />

                            <TikiButton disabled={isDisabled}>
                                {resetPasswordRequestMutation.isPending
                                    ? 'Sending...'
                                    : 'Send the Signal Fire'}
                                {!resetPasswordRequestMutation.isPending && (
                                    <SendIcon sx={{ fontSize: 20 }} />
                                )}
                            </TikiButton>

                            <Box
                                component="button"
                                type="button"
                                onClick={() => navigate('/?theme=tiki')}
                                sx={{
                                    width: '100%',
                                    py: 2,
                                    px: 3,
                                    mt: 2,
                                    background: 'transparent',
                                    color: tiki.colors.surface,
                                    fontFamily: tiki.headline,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    border: `1px solid ${tiki.colors.inputBorder}`,
                                    borderRadius: '0.75rem',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: tiki.colors.inputBg },
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 20 }} />
                                Back to the Luau
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </TikiLayout>
    );
}

export default TikiForgotPasswordPage;
